import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { FormEvent } from 'react';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { JobType } from '../../../../api/jobs.dao';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { IJobsMonitor } from '../../../../domain/jobs-monitor/jobs-monitor';
import { SortOrder } from '../../../../api/shared.dto';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { IEventDetailsDTO } from '../../../../api/events.dao';
import {
    AttendeesAction,
    AttendeesUpdateRole,
    IEventMeetingsDTO,
    IMeetingAttendees,
    IMeetingDetailsDTO,
    IMeetingFilterParams,
    IMeetingsDAO,
    TMeetingsFilterItem,
    TSavedMeetingsFilterItem,
    TSavedSelectedFilterHours,
    TSelectedFilterTypes
} from '../../../../api/meetings.dao';
import { IAttendeeDetailsDTO, IAttendeeDTO, IAttendeeOrganizationDTO, IAttendeeOrganizationListDTO } from '../../../../api/attendees.dao';
import { IGNORE_ATTENDEES, IOrganizationsDAO } from '../../../../api/organizations.dao';
import { ISubWindowFormMethods } from '../manage-event.viewmodel';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { IControlDAO } from '../../../../api/control.dao';
import { IApiDateConverter } from '../../../../infrastructure/api-date-converter';
import { isDatesEquals } from '../../../../infrastructure/utils/common.utils';
import { SCHEDULE_PAGE_SIZE_NAME } from '../../../../constants/pagination.contants';
import { getAddedAttendees } from '../../../../infrastructure/utils/attendee.utils';
import { UploadMeetingsFormDataVM } from './_upload-meetings-button/_upload-meetings-modal/_upload-meetings-form/upload-meetings-form-data.viewmodel';
import { MeetingForm } from './_meeting-form/meeting-form.container';
import { OpenFrom, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../infrastructure/global-storage';
import { SCHEDULE_TAB_SELECTED_FILTERS } from '../../../../constants/meeting.constants';
import { errorText } from '../../../../constants/common-labels.constants';
import { AssignRoomFormDataVM } from './_assign-room/_assign-room-form/assign-room-form-data.viewmodal';
import { MeetingCoPilotVM } from './_meeting-co-pilot/_meeting-co-pilot-modal/meeting-co-pilot.viewmodal';
import { AttendeeColumnPopoverViewmodel } from '../../../global/attendee-column/attendee-column-popover/attendee-column-popover.viewmodel';
import { IReportGenerationNotificationProps } from '../_reports/show-report-toast/show-report-toast';

class MeetingsListViewModel {
    @mobx.observable
    public meetingsList: IMeetingDetailsDTO[];
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    public filterHours: Date[] | null;
    @mobx.observable
    public selectedFilterHours: Date[] = [];
    public generateMeetingInteractionsReport: (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ) => void;
    public generateMeetingRegistrationReport: (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ) => void;
    @mobx.observable
    public searchQuery = '';
    @mobx.observable
    public attendees: IAttendeeOrganizationDTO[] = [];
    public withoutRoom: boolean;
    public showEventNotesPopup: (isEditMode: boolean, openFrom: OpenFrom) => void;
    public showAgentMemoryPopup: (openFrom: OpenFrom) => void;
    @mobx.observable
    public guidelinesFormSubWindow: { [key: string]: ISubWindowFacade };
    @mobx.observable
    public selectedFilterTypes = '';
    @mobx.observable
    public selectedSortOrder: SortOrder = SortOrder.Asc;
    @mobx.observable
    public isMeetingCoPilotFormSaving: boolean;
    public openRegistrantsBySessionReportModal: () => void;
    @mobx.observable
    public loadingAttendees = true;
    @mobx.observable
    private meetingFormDataVM: UploadMeetingsFormDataVM;
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    private meetingIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    @mobx.observable
    private paginationVM: PagingViewModel;
    @mobx.observable
    private isAssignRoomsFormShown: boolean;
    @mobx.observable
    private assignRoomFormDataVM: AssignRoomFormDataVM;
    @mobx.observable
    private meetingCoPilotFormDataVM: MeetingCoPilotVM | null;
    private fetchMeetingsRequestId;
    @mobx.observable
    private attendeeDetailsPopoverVM: AttendeeColumnPopoverViewmodel;
    @mobx.observable
    private isFormShown: boolean;
    @mobx.observable
    private isBulkCancelFormShown: boolean;
    @mobx.observable
    private isBulkRemoveFormShown: boolean;
    @mobx.observable
    private isSaving: boolean;

    private fetchMeetingsAbortController: AbortController | null = null;

    public constructor(
        public readonly accountId: number,
        private readonly intl: IntlShape,
        private readonly subWindowFormMethods: ISubWindowFormMethods,
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
        @IoC.Inject private readonly controlDAO: IControlDAO = IoC.Container.get(IControlDAO),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter = IoC.Container.get(IApiDateConverter),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly jobsRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
) {
        this.meetingIdToSubWindowMap = {};

        this.withoutRoom = false;

        this.setUpPagination();

        this.assignRoomFormDataVM = new AssignRoomFormDataVM();
        this.meetingFormDataVM = new UploadMeetingsFormDataVM(this.intl);
        this.fetchMeetingsRequestId = 1;

        this.jobsRuntime.onComplete(JobType.MeetingsUpload, this.handleUploadMeetingsJobCompletion);
        this.jobsRuntime.onComplete(JobType.AssignRooms, this.handleAssignRoomsJobEnding);
        this.jobsRuntime.onComplete(JobType.MeetingsCanceling, this.handleCancelMeetingsJobCompletion);
        this.jobsRuntime.onComplete(JobType.MeetingsRemoving, this.handleRemoveMeetingsJobCompletion);
        this.jobsRuntime.onComplete(JobType.EventBlueprintTrigger, this.handleEventBlueprintTriggerJobCompletion);
    }

    public get attendeDetailsVM(): AttendeeColumnPopoverViewmodel {
        return this.attendeeDetailsPopoverVM;
    }

    public isRuntimeReady = (): boolean => this.jobsRuntime.isReady();

    @mobx.action
    public initialize(): void {
        this.meetingFormDataVM.selectedTimeZone = this.getEvent().timeZone;
        this.attendees = [];
    }

    public startAssignRoomsJob = async () => {
        const job = this.jobsRuntime.getJobByType(JobType.AssignRooms);
        job.onComplete(this.handleAssignRoomsJobEnding);

        await job.trigger({
            connectionVersion: this.assignRoomFormDataVM.connectionVersion,
        });
        this.hideAndResetForm();
        this.hideAndResetAssignRoomsForm();
    };

    public composeAssignRoomsButtonProperties = (): { text: string; disabled: boolean } => {
        return {
            text: this.intl.formatMessage({
                id: 'oe.rooms.assign',
                defaultMessage: 'Assign Rooms',
                description: 'Label text'
            }),
            disabled: !this.jobsRuntime.isReady()
        };
    };

    private handleAssignRoomsJobEnding = async () => {
        await this.fetchMeetingsRequest();
        this.closeAllOpenedEditSubWindows();
    };

    public isMeetingEditFormShown(meetingId: number) {
        return (
            this.meetingIdToSubWindowMap[meetingId] &&
            this.meetingIdToSubWindowMap[meetingId].getStatus() !== SubWindowStatus.Closed
        );
    }

    public createAttendee = (callback: (attendee: IAttendeeDetailsDTO) => void) => {
        this.subWindowFormMethods.attendees.openNewSubWindow(OpenFrom.Schedule, callback);
    };

    public editAttendee = (attendeeId: number) => {
        this.subWindowFormMethods.attendees.openEditSubWindow(attendeeId, OpenFrom.Schedule, () => {
            this.fetchMeetingsRequest();
            this.fetchOrganizations(IGNORE_ATTENDEES);
        });
    };

    public getPaginationVM() {
        return this.paginationVM;
    }

    public setGenerateMeetingInteractionsReport = (
        generateMeetingInteractionsReport: (
            meetingId:number,
            reportGenerationNotificationProps: IReportGenerationNotificationProps,
        ) => Promise<void>
    ) => {
        this.generateMeetingInteractionsReport = generateMeetingInteractionsReport;
    };

    public setGenerateMeetingRegistrationReport = (
        generateMeetingRegistrationReport: (
            meetingId:number,
            reportGenerationNotificationProps: IReportGenerationNotificationProps,
        ) => Promise<void>
    ) => {
        this.generateMeetingRegistrationReport = generateMeetingRegistrationReport;
    };

    @mobx.action
    private fetchMeetings = async (requestId: number) => {
        this.loading = true;
        let meetingsListRequestResult = await this.getMeetingsListRequestResult();

        if (this.fetchMeetingsRequestId === requestId) {
            if (!meetingsListRequestResult.content.length && this.withoutRoom) {
                this.withoutRoom = false;
                this.saveSelectedFilters();
                meetingsListRequestResult = await this.getMeetingsListRequestResult();
            }
        }

        if (this.fetchMeetingsRequestId === requestId) {
            this.handleMeetingsLoaded(meetingsListRequestResult);
        }
    };

    public fetchMeetingsRequest = async (compareFiltersWithResult?: boolean) => {
        this.fetchMeetingsRequestId++;

        try {
            if (compareFiltersWithResult) {
                await this.loadSelectedFilters();
            }
            await this.fetchMeetings(this.fetchMeetingsRequestId);
        } catch (error) {
            if (error.name === 'AbortError') return;

            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        }
    };

    public fetchFilterHours = async () => {
        mobx.runInAction(() => {
            this.filterHours = null;
        });

        const response = await this.controlDAO.getMeetingStartHours({
            eventId: this.event.eventId,
            accountId: this.accountId
        });

        mobx.runInAction(() => {
            this.filterHours = response;
        });
    };

    @mobx.action
    public handleRemoveAttendeeClick = async (meeting: IMeetingDetailsDTO, attendeeId: number, organizationId: number, roleKey: string) => {

        [meeting[roleKey]] = [meeting[roleKey]].map((item: IAttendeeOrganizationDTO[]) => {
            return this.filteringNotEmptyOrganizations(item, attendeeId);
        });

        const removedAttendee: IMeetingAttendees[] = [{
            organizationId: organizationId,
            attendeeId: attendeeId,
            action: AttendeesAction.Remove,
            role: AttendeesUpdateRole[roleKey]
        }];

       await this.updateAttendees(removedAttendee, meeting);
    };

    public filteringNotEmptyOrganizations = (
        organizations: IAttendeeOrganizationDTO[],
        attendeeId: number
    ): IAttendeeOrganizationDTO[] => {
        return organizations.filter((organization: IAttendeeOrganizationDTO) => {
            if (organization.attendees.length !== 0) {
                return this.removeAttendeeFromOrganization(organization, attendeeId);
            } else {
                return organization;
            }
        });
    };

    public removeAttendeeFromOrganization = (organization: IAttendeeOrganizationDTO, attendeeId: number) => {
        organization.attendees = organization.attendees.filter(
            (attendee: IAttendeeDTO) => attendee.attendeeId !== attendeeId
        );

        if (organization.attendees.length) {
            return organization;
        }
    };

    @mobx.action
    public updateMeetingAttendeesFromMeetingList = (
        meeting: IMeetingDetailsDTO,
        attendees: IAttendeeOrganizationDTO[],
        roleKey: string
    ): void => {
        const organizationsAttendeesBeforeChange = [...meeting[roleKey]];
        meeting[roleKey] = attendees;
        const organizationsAttendeesAfterChange = [...meeting[roleKey]];
        const addedAttendees = getAddedAttendees(organizationsAttendeesAfterChange, organizationsAttendeesBeforeChange, roleKey);
        if (addedAttendees.length) {
            this.updateAttendees(addedAttendees, meeting);
        }
    };

    public updateAttendees = async (updatedAttendees: IMeetingAttendees[], meeting: IMeetingDetailsDTO) => {
        try {
            const response = await this.meetingsDAO.updateMeetingAttendees({operations: updatedAttendees}, {
                accountId: this.accountId,
                eventId: this.event.eventId,
                meetingId: meeting.meetingId
            });

            mobx.runInAction(() => {
                this.updateMeetingListByIndex(meeting.meetingId, response);
            });

        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        }
    };

    @mobx.action
    public updateMeetingListByIndex = (meetingId: number, updatedMeeting: IMeetingDetailsDTO ) => {
        const indexForUpdating = this.meetingsList.findIndex(item => item.meetingId === meetingId);

        this.meetingsList[indexForUpdating] = { ...updatedMeeting };
    };

    @mobx.action
    public updateMeetingAttendeesFromAttendeeForm = (
        meeting: IMeetingDetailsDTO,
        attendees: IAttendeeOrganizationDTO[],
        roleKey: string
    ) => {
        meeting[roleKey] = attendees;
        this.updateMeeting(meeting);
    };

    @mobx.action
    public updateMeeting = async (meeting: IMeetingDetailsDTO) => {
        try {
            const response = await this.meetingsDAO.updateMeetingDetails(meeting, {
                accountId: this.accountId,
                eventId: this.event.eventId,
                meetingId: meeting.meetingId
            });

             mobx.runInAction(() => {
                this.updateMeetingListByIndex(meeting.meetingId, response);
            });

        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);

        }
    };

    public cancelFetchMeetingsRequest = (): void => {
        if (this.fetchMeetingsAbortController && !this.fetchMeetingsAbortController.signal.aborted) {
            this.fetchMeetingsAbortController.abort();
        }
    };

    private getMeetingsListRequestResult = async (): Promise<IEventMeetingsDTO> => {
        const meetingStartHours = this.selectedFilterHours.map(hour => this.apiDateConverter.dateToAPIString(hour)).join();
        const params: IMeetingFilterParams = {
            eventId: this.event.eventId,
            accountId: this.accountId,
            timeZone: this.event.timeZone
        };
        if (this.selectedFilterTypes) {
            params.types = this.selectedFilterTypes;
        }

        // cancel previous request if exists
        this.cancelFetchMeetingsRequest();
        this.fetchMeetingsAbortController = new AbortController();

        return await this.meetingsDAO.getEventMeetings({
            ...params,
            ignoreRegistrants: IGNORE_ATTENDEES,
            sortFields: [
                {
                    order: this.selectedSortOrder,
                    field: 'startDateTime'
                },
                {
                    order: SortOrder.Desc,
                    field: 'status'
                },
                {
                    order: SortOrder.Desc,
                    field: 'organizations.role'
                },
                {
                    order: SortOrder.Asc,
                    field: 'organizations.organization.name'
                },
            ],
            page: this.paginationVM.getCurrentPageForBE(),
            size: this.paginationVM.getPageSize(),
            withoutRoom: this.withoutRoom,
            meetingStartHour: meetingStartHours,
        },
            this.fetchMeetingsAbortController.signal
        );
    };

    public setWithoutRoom = param => {
        if (this.withoutRoom !== param) this.trackingService.get().track(TRACKING_EVENTS.SCHEDULE_ROOMS_FILTER,{});
        this.withoutRoom = param;
        this.saveSelectedFilters();
    };

    public isLoading = () => {
        return this.loading;
    };

    public getMeetingsList = () => {
        return this.meetingsList;
    };

    public getEvent = () => {
        return this.event as Readonly<IEventDetailsDTO>;
    };

    public downloadMeetingsCSV = async () => {
        await this.meetingsDAO.getMeetingsCSV({
            eventId: this.event.eventId,
            accountId: this.accountId
        });
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_SCHEDULE, {});
    };

    @mobx.action
    public handleEditClick = (meetingId: number, openFrom?: OpenFrom) => {
        const existingSubWindowForThisMeeting = this.meetingIdToSubWindowMap[meetingId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisMeeting && existingSubWindowForThisMeeting.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisMeeting.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.meetingIdToSubWindowMap[meetingId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage({
                    id: 'oe.meeting.edit',
                    defaultMessage: 'Edit Meeting',
                    description: 'Label text'
                }),
                componentToHost: MeetingForm,
                componentToHostProps: {
                    event: this.event,
                    accountId: this.accountId,
                    intl: this.intl,
                    meetingId: meetingId,
                    subWindowFormMethods: this.subWindowFormMethods,
                    saveCompletionCallback: this.completeMeetingUpdate,
                    deleteCompletionCallback: () => this.completeMeetingDeletion(meetingId),
                    createAttendee: this.createAttendee,
                    addNewAttendeeByRole: this.addNewAttendeeByRole,
                    openFrom: openFrom,
                },
            });
        }
    };

    public handleCreateClick = () => {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.meeting.new',
                defaultMessage: 'New Meeting',
                description: 'Label text'
            }),
            componentToHost: MeetingForm,
            componentToHostProps: {
                accountId: this.accountId,
                event: this.event,
                intl: this.intl,
                meetingId: null,
                subWindowFormMethods: this.subWindowFormMethods,
                saveCompletionCallback: () => this.fetchMeetingsRequest(),
                createAttendee: this.createAttendee,
                addNewAttendeeByRole: this.addNewAttendeeByRole,
            }
        });
    };

    public getMeetingFormDataVM(): UploadMeetingsFormDataVM {
        return this.meetingFormDataVM;
    }

    public getCSVFileName = () => {
        return this.meetingFormDataVM.meetingsField ? this.meetingFormDataVM.meetingsField.name : '';
    };

    public isMeetingsListEmpty() {
        return this.meetingsList?.length === 0;
    }

    @mobx.action
    private completeMeetingDeletion = (deletedMeetingId: number) => {
        if (this.meetingsList && this.selectedFilterHours) {
            const deletedMeeting = this.meetingsList.find(el => el.meetingId === deletedMeetingId);
            if (this.selectedFilterHours.find(el => isDatesEquals(el, deletedMeeting.startDateTime)) &&
                this.meetingsList.filter(el => isDatesEquals(el.startDateTime, deletedMeeting.startDateTime)).length === 1) {
                this.selectedFilterHours = this.selectedFilterHours.filter(el => !isDatesEquals(el, deletedMeeting.startDateTime));
                this.saveSelectedFilters();
            }
        }

        this.closeEditForm(deletedMeetingId.toString());
        this.fetchMeetingsRequest();
    };

    @mobx.action
    private completeMeetingUpdate = (newMeetingDetails: IMeetingDetailsDTO) => {
        if (this.meetingsList && this.selectedFilterHours && newMeetingDetails.meetingId) {
            const { meetingId, startDateTime: newStartDateTime } = newMeetingDetails;
            const oldMeeting = this.meetingsList.find(el => el.meetingId === meetingId);
            if (oldMeeting &&
                this.selectedFilterHours.find(el => isDatesEquals(el, oldMeeting.startDateTime)) &&
                this.meetingsList.filter(el => isDatesEquals(el.startDateTime, oldMeeting.startDateTime)).length === 1 &&
                !isDatesEquals(oldMeeting.startDateTime, newStartDateTime)) {
                this.selectedFilterHours = this.selectedFilterHours.filter(el => !isDatesEquals(el, oldMeeting.startDateTime));
            }
        }
        this.fetchMeetingsRequest();
    };

    @mobx.action
    private handleMeetingsLoaded = (eventMeetingsDTO: IEventMeetingsDTO) => {
        this.meetingsList = eventMeetingsDTO.content;
        this.loading = false;
        this.paginationVM.setTotalRecords(eventMeetingsDTO.totalElements);
    };

    private closeEditForm(id: string) {
        this.meetingIdToSubWindowMap[id].close();
    }

    @mobx.action.bound
    public closeAllOpenedEditSubWindows() {
        for (const meetingId of Object.keys(this.meetingIdToSubWindowMap)) {
            this.closeEditForm(meetingId);
        }
    }

    @mobx.action
    private setUpPagination() {
        this.paginationVM = new PagingViewModel({ withChooser: true, pageSizeStorageName: SCHEDULE_PAGE_SIZE_NAME });
        mobx.reaction(() => [this.paginationVM.getCurrentPage(), this.paginationVM.getPageSize()], () => this.fetchMeetingsRequest());
    }

    @mobx.action
    public showForm = () => {
        this.isFormShown = true;
    };


    public composeUploadMeetingButtonProperties(): { text: string; disabled: boolean } {
        return {
            text: this.intl.formatMessage({
                id: 'oe.schedule.upload',
                defaultMessage: 'Upload Schedule',
                description: 'Label text',
            }),
            disabled: !this.jobsRuntime.isReady()
        };
    }

    @mobx.action
    private handleUploadMeetingsJobCompletion = async (): Promise<void> => {
        this.hideAndResetForm();
        this.closeAllOpenedEditSubWindows();

        await this.fetchMeetingsRequest();
        await this.fetchOrganizations(IGNORE_ATTENDEES);
    };

    @mobx.action
    private handleCancelMeetingsJobCompletion = async (): Promise<void> => {
        await this.fetchMeetingsRequest();
        this.closeAllOpenedEditSubWindows();
    };

    @mobx.action
    private handleRemoveMeetingsJobCompletion = async (): Promise<void> => {
        await this.fetchMeetingsRequest();
        this.closeAllOpenedEditSubWindows();
    };

    @mobx.action
    private handleEventBlueprintTriggerJobCompletion = async (): Promise<void> => {
        await this.fetchMeetingsRequest();
    };

    @mobx.action
    public hideAndResetForm = () => {
        if (!this.isSaving) {
            this.isFormShown = false;
            this.resetUploadMeetingsFormData();
        }
    };

    public getIsFormShown = () => {
        return this.isFormShown;
    };

    @mobx.action
    public handleUploadMeetingsClick = async (e: FormEvent) => {
        e.preventDefault();
        if (!this.meetingFormDataVM.isFormValid()) {
            this.meetingFormDataVM.allowToShowValidationErrors();
            return;
        }

        if (this.isSaving) {
            return;
        }

        this.isSaving = true;

        const job = this.jobsRuntime.getJobByType(JobType.MeetingsUpload);
        job.onComplete(this.handleUploadMeetingsJobCompletion);

        try {
            await job.trigger({
                accountId: this.accountId,
                eventId: this.getEvent().eventId,
                timezone: this.meetingFormDataVM.selectedTimeZone,
                separator: this.meetingFormDataVM.separatorToken,
                file: this.meetingFormDataVM.meetingsField,
                defaultDuration: this.meetingFormDataVM.duration,
                dateFormatType: this.meetingFormDataVM.dateFormatType,
                connectionVersion: this.meetingFormDataVM.connectionVersion,
            });
            mobx.runInAction(() => {
                this.isFormShown = false;
                this.resetUploadMeetingsFormData();
            });
        } catch {
            mobx.runInAction(() => {
                this.meetingFormDataVM.setMeetingsField(null);
                this.meetingFormDataVM.setDate(Date.now());
            });
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @mobx.action
    private resetUploadMeetingsFormData = (): void => {
        this.meetingFormDataVM = new UploadMeetingsFormDataVM(this.intl);
        this.meetingFormDataVM.selectedTimeZone = this.event.timeZone;
    };

    public reCallDataList = async (): Promise<void> => {
        await this.fetchMeetingsRequest();
        await this.fetchOrganizations(IGNORE_ATTENDEES);
    };

    @mobx.action
    public applyFilterHours = (hours: Date[], sortOrder: SortOrder) => {
        if (this.selectedFilterHours !== hours) this.trackingService.get().track(TRACKING_EVENTS.SCHEDULE_DATE_TIME_FILTER,{});
        this.selectedFilterHours = hours;
        this.selectedSortOrder = sortOrder;
        this.saveSelectedFilters();
        this.fetchMeetingsRequest();
    };

    @mobx.action
    public setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    @mobx.action
    public clearFilters = () => {
        this.selectedFilterTypes = '';
    };

    @mobx.action
    public onMount = (): void => {
        this.searchQuery = '';
        this.paginationVM.selectPage(1);
        this.cancelFetchMeetingsRequest();
        this.fetchMeetingsRequest(true);
        this.attendeDetailsVM?.setClose();
    };

    public onUnmount = (): void => {
        this.cancelFetchMeetingsRequest();
    };

    public async fetchOrganizations(ignoreAttendees: boolean) {
        const organizationsList: IAttendeeOrganizationListDTO = await this.organizationDAO.getAllOrganizations({
            eventId: this.event.eventId,
            accountId: this.accountId,
            ignoreAttendees: ignoreAttendees
        });

        mobx.runInAction(() => {
            this.attendees = organizationsList.content;
        });

        return organizationsList;
    }

    public addNewAttendeeByRole = async (meeting: IMeetingDetailsDTO, attendee: IAttendeeDetailsDTO, roleKey: string) => {
        const arr = [...meeting[roleKey]];
        const updatedOrganization: IAttendeeOrganizationDTO = arr.find(({ organizationId }) => organizationId === attendee.organizationId);
        if (updatedOrganization) {
            mobx.runInAction(() => {
                updatedOrganization.attendees.push(attendee);
            });
        } else {
            const organizationsList = await this.fetchOrganizations(IGNORE_ATTENDEES);

            mobx.runInAction(() => {
                arr.push({
                    ...organizationsList.content.find(({ organizationId }) => organizationId === attendee.organizationId),
                    attendees: [attendee]
                });
            });
        }
        this.updateMeetingAttendeesFromAttendeeForm(meeting, arr, roleKey);
    };

    @mobx.action
    public showBulkCancelForm = () => {
        this.isBulkCancelFormShown = true;
    };

    public getIsBulkCancelFormShown = () => {
        return this.isBulkCancelFormShown;
    };

    @mobx.action
    public hideBulkCancelMeetingsForm = () => {
        this.isBulkCancelFormShown = false;
    };

    @mobx.action
    public triggerBulkCancelMeetings = async (dates: string[]) => {
        const job = this.jobsRuntime.getJobByType(JobType.MeetingsCanceling);
        job.onComplete(this.handleCancelMeetingsJobCompletion);

        await job.trigger({ dates: dates });
        mobx.runInAction(() => this.hideBulkCancelMeetingsForm());
    };

    @mobx.action
    public showBulkRemoveForm = () => {
        this.isBulkRemoveFormShown = true;
    };

    public getIsBulkRemoveFormShown = () => {
        return this.isBulkRemoveFormShown;
    };

    @mobx.action
    public hideBulkRemoveMeetingsForm = () => {
        this.isBulkRemoveFormShown = false;
    };

    @mobx.action
    public triggerBulkRemoveMeetings = async (dates: string[]): Promise<void> => {
        const job = this.jobsRuntime.getJobByType(JobType.MeetingsRemoving);
        job.onComplete(this.handleRemoveMeetingsJobCompletion);

        await job.trigger({ dates: dates });
        mobx.runInAction(() => this.hideBulkRemoveMeetingsForm());
    };

    @mobx.action
    public handleChangeTypeFilter = (types: string) => {
        if (this.selectedFilterTypes !== types) this.trackingService.get().track(TRACKING_EVENTS.SCHEDULE_MEETING_TYPE_FILTER,{});
        this.selectedFilterTypes = types;
        this.saveSelectedFilters();
        this.fetchMeetingsRequest();
    };

    public fetchMeetingTypes = async (): Promise<{ id: string; label: string }[]> => {
        const params = { accountId: this.accountId, eventId: this.event?.eventId };
        const response = await this.controlDAO.getMeetingTypes(params);
        const completeTypes = response.map(type => type?.trim()).filter(type => !!type);

        return completeTypes.map(type => {
            return {
                id: type,
                label: type,
            };
        });

    };

    public trackCopyAttendeeInfoPopoverButtonMixPanel = (eventName: string): void => {
        this.trackingService.get().track(TRACKING_EVENTS.COPY_ATTENDEE_NAME_FROM_POPOVER, {
            [eventName]: OpenFrom.Schedule,
        });
    };
    public setDownloadRegistrantsBySessionReport = (openReportModal: () => void) => {
        this.openRegistrantsBySessionReportModal = openReportModal;
    };

    public getSelectedFilters = (): TMeetingsFilterItem[] => {
        return [
            this.selectedFilterTypes.length && {
                filterName: 'selectedFilterTypes',
                filterValues: this.selectedFilterTypes
            },
            {
                filterName: 'withoutRoom',
                filterValues: this.withoutRoom
            },
            this.selectedFilterHours.length && {
                filterName: 'selectedFilterHours',
                filterValues: this.selectedFilterHours
            },
            {
                filterName: 'selectedSortOrder',
                filterValues: this.selectedSortOrder
            }
        ].filter(Boolean) as TMeetingsFilterItem[];
    };

    private saveSelectedFilters = (): void => {
        try {
            const selectedFilters = this.getSelectedFilters();
            const currentFilters: TSavedMeetingsFilterItem = JSON.parse(localStorage.getItem(SCHEDULE_TAB_SELECTED_FILTERS)) ?? {};
            currentFilters[this.event.eventId] = selectedFilters;
            localStorage.setItem(SCHEDULE_TAB_SELECTED_FILTERS, JSON.stringify(currentFilters));
        } catch (error) {
            localStorage.removeItem(SCHEDULE_TAB_SELECTED_FILTERS);
        }
    };

    @mobx.action
    private compareSavedHoursWithResultHours = (savedSelectedFilterHours: TSavedSelectedFilterHours | undefined): void => {
        if (!savedSelectedFilterHours) return;

        savedSelectedFilterHours.filterValues = savedSelectedFilterHours.filterValues
            .filter(h => {
                return this.filterHours.find(f => {
                    return f.toISOString() === h;
                });
            })
        ;
    };

    @mobx.action
    private compareSavedTypesWithResultTypes = (
        savedSelectedFilterTypes: TSelectedFilterTypes | undefined,
        meetingsTypes: { id: string; label: string }[]
    ): void => {
        if (!savedSelectedFilterTypes) return;

        savedSelectedFilterTypes.filterValues = savedSelectedFilterTypes.filterValues
            .split(',')
            .filter(savedType => {
                return meetingsTypes.find(type => {
                    return type.id === savedType;
                });
            })
            .join(',')
        ;
    };

    public loadSelectedFilters = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.loading = true;
            });

            const shceduleTabFilters: TSavedMeetingsFilterItem = JSON.parse(localStorage.getItem(SCHEDULE_TAB_SELECTED_FILTERS));
            if (!shceduleTabFilters) return;

            const savedEventFilters = shceduleTabFilters[this.event.eventId];
            if (!savedEventFilters) return;

            const [ meetingsTypes ] = await Promise.all([this.fetchMeetingTypes(), this.fetchFilterHours()]);
            this.compareSavedHoursWithResultHours(
                savedEventFilters.find(f => f.filterName === 'selectedFilterHours') as TSavedSelectedFilterHours
            );
            this.compareSavedTypesWithResultTypes(
                savedEventFilters.find(f => f.filterName === 'selectedFilterTypes') as TSelectedFilterTypes,
                meetingsTypes
            );
            this.applySavedFilters(savedEventFilters);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        }
    };

    public getAssignRoomFormDataVM(): AssignRoomFormDataVM {
        return this.assignRoomFormDataVM;
    };

    @mobx.action
    public showAssignRoomsForm = (): void => {
        this.isAssignRoomsFormShown = true;
    };

    public getIsAssignRoomsFormShown = (): boolean => {
        return this.isAssignRoomsFormShown;
    };

    @mobx.action
    public hideAndResetAssignRoomsForm = (): void => {
        this.isAssignRoomsFormShown = false;
        this.resetAssignRoomFormData();
    };

    public getMeetingCoPilotFormDataVM(): MeetingCoPilotVM {
        return this.meetingCoPilotFormDataVM;
    };

    @mobx.action
    public showMeetingCoPilotForm = (): void => {
        this.meetingCoPilotFormDataVM = new MeetingCoPilotVM(this.accountId, this.getEvent(), this.fetchMeetingsRequest);
        this.meetingCoPilotFormDataVM.openModal();
    };

    @mobx.action
    public getMeetingDetails = async (meetingId: number): Promise<void> => {
        try {
            const meetingInfo = await this.meetingsDAO.getMeetingDetails({
                eventId: this.event.eventId,
                meetingId: meetingId,
                accountId: this.accountId,
            });
            const currentMeeting = this.meetingsList.find(m => m.meetingId === meetingId);
            mobx.runInAction(() => {
                currentMeeting.registrants = meetingInfo.registrants;
            });

        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        }
    };

    public fetchAttendeeListHandler = async (meetingId: number): Promise<void> => {
        await Promise.all([
            this.getMeetingDetails(meetingId),
            this.fetchOrganizations(!IGNORE_ATTENDEES)
        ]);
        mobx.runInAction(() => {
            this.loadingAttendees = false;
        });
    };

    @mobx.action
    public resetLoadingState = (): void => {
        this.loadingAttendees = true;
    };

    public initAttendeDetailsPopoverVM() {
        this.attendeeDetailsPopoverVM = new AttendeeColumnPopoverViewmodel(
            this.accountId,
            this.getEvent().eventId,
            this.editAttendee,
            this.trackCopyAttendeeInfoPopoverButtonMixPanel
        );
    };

    @mobx.action
    private resetMeetingCoPilotFormData = (): void => {
        this.meetingCoPilotFormDataVM = null;
    };

    @mobx.action
    private resetAssignRoomFormData = (): void => {
        this.assignRoomFormDataVM = new AssignRoomFormDataVM();
    };

    @mobx.action
    private applySavedFilters = (filters: TMeetingsFilterItem[]): void => {
        if (filters) {
            filters.forEach(({ filterName, filterValues}) => {
                switch (filterName) {
                    case 'selectedFilterHours':
                        this[filterName] = filterValues.map(d => new Date(d));
                        break;
                    default:
                        // @ts-ignore
                        this[filterName] = filterValues;
                }
            });
        }
    };
}

export { MeetingsListViewModel };
